import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useDispatch } from "react-redux";
// The package ships no typed default export, so the resolver flags this import
// even though it is correct. Same import as Returns/components/BottomSheet.
// eslint-disable-next-line import/default
import RBSheet from "react-native-raw-bottom-sheet";
import Text from "@components/CustomText";
import PrimaryButton from "@components/PrimaryButton";
import color from "@constants/color";
import { useCachedLocation } from "@components/common/LocationHook";
import { GIGS_COPY } from "./copy";
import { actions as homeWidgetActions } from "@reducers/homeWidgetReducer";
import {
  getGigsAvailability,
  submitGigsAvailability,
} from "@services/api/Gigs";
import DayRow from "./components/DayRow";
import SummaryBar from "./components/SummaryBar";
import ConfirmationSheet from "./components/ConfirmationSheet";
import {
  computeChangedDays,
  deriveLocalSummary,
  formatGigDayLabel,
  formatTimeLabel,
  isConfirmEnabled,
  localTodayIso,
} from "./utils";
import type {
  GigsAvailabilityResponse,
  GigsNavigation,
  GigsAvailabilitySubmitResponse,
  LocalSelection,
} from "./types";

/**
 * Screen 2 — "Working the next N days?". The server owns every date/lock
 * computation; this screen only renders what `GET /cpdb/gigs` sends and
 * submits a diff of what changed locally.
 */
const MarkingScreen: React.FC = () => {
  const navigation = useNavigation() as unknown as GigsNavigation;
  const dispatch = useDispatch();
  const getCachedLocation = useCachedLocation();
  const sheetRef = useRef<RBSheet>(null);

  const [serverData, setServerData] = useState<GigsAvailabilityResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [localSelections, setLocalSelections] = useState<
    Record<string, LocalSelection>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitResponse, setSubmitResponse] =
    useState<GigsAvailabilitySubmitResponse | null>(null);

  const loadAvailability = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    getGigsAvailability()
      .then(({ data }) => {
        setServerData(data);
        setLocalSelections({});
      })
      .catch(() => {
        setLoadError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  const days = useMemo(() => serverData?.days ?? [], [serverData]);
  const todayIso = localTodayIso();

  const handleSelectionChange = useCallback(
    (gigDate: string, response: "YES" | "NO") => {
      setLocalSelections((prev) => ({ ...prev, [gigDate]: response }));
    },
    [],
  );

  const summaryText = useMemo(() => {
    const summary = deriveLocalSummary(localSelections, days);
    return GIGS_COPY.marking.summary(summary.yes, summary.no, summary.pending);
  }, [localSelections, days]);

  const confirmEnabled = useMemo(
    () => isConfirmEnabled(localSelections, days, submitting),
    [localSelections, days, submitting],
  );

  // Just the spoke name: the shift time is not shown at the top.
  const dutyLocation = serverData?.duty?.location_name ?? null;

  // The server sends lock_time only while the cutoff is on; it is off for
  // now, so this is the no-show line alone.
  const footerCopy = useMemo(() => {
    const lockTimeLabel = formatTimeLabel(serverData?.duty?.lock_time);
    return lockTimeLabel
      ? GIGS_COPY.marking.footerWithTime(lockTimeLabel)
      : GIGS_COPY.marking.footerNoTime;
  }, [serverData]);

  const handleConfirm = useCallback(() => {
    if (!serverData) return;
    const changedDays = computeChangedDays(localSelections, serverData.days);
    if (changedDays.length === 0 || submitting) return;

    setSubmitting(true);
    // Cache-only read — never wakes GPS, never prompts. All-null when nothing
    // was cached today; the submit goes through unconditionally either way.
    const coords = getCachedLocation();
    submitGigsAvailability({
      days: changedDays,
      location: {
        lat: coords.latitude,
        lng: coords.longitude,
        accuracy_m: coords.accuracy,
        captured_at: coords.updated_at,
      },
      platform: Platform.OS === "ios" ? "ios" : "android",
    })
      .then(({ data }) => {
        setServerData({
          duty: data.duty,
          days: data.days,
          summary: data.summary,
        });
        setLocalSelections({});
        setSubmitResponse(data);
        // Patch the home card directly from the response — no widgets refetch.
        // UPDATE_HOME_PAGE_WIDGET merges by key into state.widgets, so the
        // payload must itself be keyed "gigs".
        dispatch(homeWidgetActions.updateHomePageWidget({ gigs: data.widget }));
        sheetRef.current?.open();
      })
      .catch(() => {
        // Submit failed — local edits are kept so the CP can retry without
        // re-tapping everything.
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [serverData, localSelections, submitting, getCachedLocation, dispatch]);

  const handleSheetChange = useCallback(() => {
    sheetRef.current?.close();
  }, []);

  const handleSheetGotIt = useCallback(() => {
    sheetRef.current?.close();
    navigation.popToTop();
  }, [navigation]);

  if (loading) {
    return (
      <View style={styles.centered} testID="gigs-marking-loading">
        <ActivityIndicator color={color.primary} size="large" />
      </View>
    );
  }

  if (loadError || !serverData) {
    return (
      <View style={styles.centered} testID="gigs-marking-error">
        <Text variant="body_text_2" style={styles.errorText}>
          {GIGS_COPY.marking.loadError}
        </Text>
        <PrimaryButton
          text={GIGS_COPY.marking.retry}
          onPress={loadAvailability}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Text variant="heading_24" style={styles.heading}>
          {GIGS_COPY.marking.heading(days.length)}
        </Text>
        {dutyLocation ? (
          <Text variant="body_text_2" style={styles.subheader}>
            {dutyLocation}
          </Text>
        ) : null}

        <View style={styles.rows}>
          {days.map((day) => (
            <DayRow
              key={day.gig_date}
              day={day}
              label={formatGigDayLabel(day.gig_date, todayIso)}
              // A local edit wins, otherwise show what is already saved.
              // Without the day.response fallback an already-marked day
              // renders with both pills unselected.
              selection={
                localSelections[day.gig_date] ?? day.response ?? "UNSET"
              }
              onChange={handleSelectionChange}
            />
          ))}
        </View>

        <Text variant="caption_2" style={styles.footerCopy}>
          {footerCopy}
        </Text>
      </ScrollView>

      <SummaryBar
        summaryText={summaryText}
        confirmEnabled={confirmEnabled}
        submitting={submitting}
        onConfirm={handleConfirm}
      />

      <ConfirmationSheet
        ref={sheetRef}
        response={submitResponse}
        onChange={handleSheetChange}
        onGotIt={handleSheetGotIt}
      />
    </View>
  );
};

export default MarkingScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 16,
  },
  errorText: {
    color: color.mediumBlack,
    textAlign: "center",
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  heading: {
    color: color.mediumBlack,
  },
  subheader: {
    color: color.lightMediumBlack,
    marginTop: 4,
  },
  rows: {
    marginTop: 20,
  },
  footerCopy: {
    color: color.lightMediumBlack,
    marginTop: 16,
  },
});
